import {
  DailyPlanEntry,
  OPERATIONAL_ACTIVE_STATUSES,
  TimelineBatch,
  TimelineModification,
} from "../../../types/mes";
import {
  clockTimeToMinutes,
  extractCalendarDate,
} from "../../../utils/processParameterSerialize";
import { compareLocale, dateLocale, t } from "../../../i18n/locale";
import { phaseName, phaseRank } from "./phaseDisplay";

export type GanttGroupBy = "machine" | "phase" | "mixer" | "oven";

/**
 * Groupings where a lane is one device and a row is one whole batch, so the
 * batch's entire phase chain is drawn on the lane of its mixer/oven.
 */
export type GanttBatchGroupBy = "mixer" | "oven";

/**
 * Groupings where a lane is a resource and a bar is a single phase, so the
 * phases of one batch spread across the lanes that actually run them.
 */
export type GanttResourceGroupBy = "machine" | "phase";

export function isGanttResourceGrouping(
  groupBy: GanttGroupBy
): groupBy is GanttResourceGroupBy {
  return groupBy === "machine" || groupBy === "phase";
}

export interface GanttSwimLane {
  deviceName: string;
  batches: Partial<TimelineBatch>[];
}

function bakingStartMs(batch: Partial<TimelineBatch>): number {
  if (!batch.BakingStart) return Number.MAX_SAFE_INTEGER;
  return new Date(batch.BakingStart).getTime();
}

export function getBatchGroupName(
  batch: Partial<TimelineBatch>,
  groupBy: GanttBatchGroupBy
): string {
  if (groupBy === "oven") return batch.OvenName || t("Nepoznato");
  return batch.MixerName || t("Nepoznato");
}

export function sortBatchesForGanttLane(
  batches: Partial<TimelineBatch>[],
  _groupBy?: GanttBatchGroupBy
): Partial<TimelineBatch>[] {
  return [...batches].sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0));
}

export function buildGanttSwimLanes(
  batches: Partial<TimelineBatch>[],
  groupBy: GanttBatchGroupBy
): GanttSwimLane[] {
  const groups: Record<string, Partial<TimelineBatch>[]> = {};
  batches.forEach((batch) => {
    const name = getBatchGroupName(batch, groupBy);
    if (!groups[name]) groups[name] = [];
    groups[name].push(batch);
  });

  return Object.entries(groups)
    .map(([deviceName, batchList]) => ({
      deviceName,
      batches: sortBatchesForGanttLane(batchList, groupBy),
    }))
    .sort((a, b) => {
      if (groupBy === "oven") {
        const aStart = a.batches[0] ? bakingStartMs(a.batches[0]) : 0;
        const bStart = b.batches[0] ? bakingStartMs(b.batches[0]) : 0;
        return aStart - bStart || a.deviceName.localeCompare(b.deviceName, compareLocale());
      }
      return a.deviceName.localeCompare(b.deviceName, compareLocale());
    });
}

/** One phase segment of a batch, normalised for drawing. */
export interface GanttPhaseRef {
  code: string;
  name?: string;
  start?: string;
  end?: string;
  machineId: number | null;
  workers: number | null;
  /** Shares its time window with another phase of the same routing step. */
  parallel: boolean;
}

/** A batch inside a lane, together with the segments that belong to that lane. */
export interface GanttLaneRow {
  batch: Partial<TimelineBatch>;
  phases: GanttPhaseRef[];
}

export interface GanttLane {
  laneName: string;
  rows: GanttLaneRow[];
}

/** Lane collecting phases the routing never tied to a machine (proofing, resting…). */
export const NO_MACHINE_LANE = "Bez uređaja";
export const noMachineLane = (): string => t(NO_MACHINE_LANE);

/**
 * Machine a phase runs on.
 *
 * The stored segment wins. Where the routing left it empty we fall back to the
 * batch's legacy device columns, which still carry the mixer, processing line
 * and oven — without that, a third of the segments would pile up in the
 * "no device" lane even though the batch clearly names the machine.
 */
function resolvePhaseMachine(
  batch: Partial<TimelineBatch>,
  code: string,
  segmentMachineId?: number | null
): number | null {
  if (segmentMachineId != null) return segmentMachineId;
  if (code === "mixing") return batch.MixerMachineID ?? null;
  if (code === "processing") return batch.ProcessingMachineID ?? null;
  if (code === "baking") return batch.OvenMachineID ?? null;
  return null;
}

/** Same fallback for the crew size, which the legacy model keeps per batch. */
function resolvePhaseWorkers(
  batch: Partial<TimelineBatch>,
  code: string,
  segmentWorkers?: number | null
): number | null {
  if (segmentWorkers != null) return segmentWorkers;
  if (code === "mixing") return batch.MixingWorkers ?? null;
  if (code === "processing") return batch.ProcessingWorkers ?? null;
  if (code === "baking") return batch.BakingWorkers ?? null;
  return null;
}

/**
 * Phase segments of a batch, from the dynamic phase model when present.
 *
 * Falls back to the legacy 5 columns when the dynamic phases carry no usable
 * times. That covers products without a routing and plans saved before
 * TimelineBatchPhase existed, but also frozen batches: the rescheduler skips
 * them, so the phase rows seeded from their routing keep NULL times while the
 * legacy columns still hold the real schedule. Trusting the empty phases there
 * would drop the batch out of the chart entirely.
 */
export function batchPhaseRefs(batch: Partial<TimelineBatch>): GanttPhaseRef[] {
  const scheduled = batch.Phases?.some((p) => p.Start && p.End) ?? false;
  const source: Array<Partial<GanttPhaseRef> & { code: string }> =
    batch.Phases && scheduled
      ? batch.Phases.map((p) => ({
          code: p.Code,
          name: p.Name,
          start: p.Start,
          end: p.End,
          machineId: p.MachineID ?? null,
          workers: p.Workers ?? null,
          parallel: p.Parallel ?? false,
        }))
      : [
          { code: "mixing", start: batch.MixingStart, end: batch.MixingEnd },
          {
            code: "bulk_fermentation",
            start: batch.FermentationStart,
            end: batch.FermentationEnd,
          },
          { code: "processing", start: batch.ProcessingStart, end: batch.ProcessingEnd },
          { code: "final_proof", start: batch.FinalProofStart, end: batch.FinalProofEnd },
          { code: "baking", start: batch.BakingStart, end: batch.BakingEnd },
        ];

  return source.map((segment) => ({
    code: segment.code,
    name: segment.name,
    start: segment.start,
    end: segment.end,
    machineId: resolvePhaseMachine(batch, segment.code, segment.machineId),
    workers: resolvePhaseWorkers(batch, segment.code, segment.workers),
    parallel: segment.parallel ?? false,
  }));
}

/**
 * Whether a segment is worth drawing.
 *
 * Zero-length segments are not phases: the legacy 5-column fallback gives a
 * semi-finished product a 0-minute processing/proof/baking it never performs,
 * and a routing can carry a leftover 0-minute phase. Drawn at the chart's 2px
 * minimum they claim a machine is occupied, and they would open a resource lane
 * ("Etažna nova") that holds nothing.
 */
export function isDrawablePhase(phase: GanttPhaseRef): boolean {
  const startDate = extractCalendarDate(phase.start);
  const endDate = extractCalendarDate(phase.end);
  if (startDate && endDate) {
    const start = absoluteMinutes(phase.start, startDate);
    const end = absoluteMinutes(phase.end, startDate);
    return start !== null && end !== null && end > start;
  }
  const start = clockTimeToMinutes(phase.start);
  const end = clockTimeToMinutes(phase.end);
  return start !== null && end !== null && end > start;
}

const MS_PER_DAY = 86_400_000;
const MINUTES_PER_DAY = 24 * 60;

function calendarDateUtc(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Shift a yyyy-mm-dd value by a whole number of calendar days. */
export function addCalendarDays(date: string, days: number): string {
  const utc = new Date(calendarDateUtc(date) + days * MS_PER_DAY);
  const y = utc.getUTCFullYear();
  const m = (utc.getUTCMonth() + 1).toString().padStart(2, "0");
  const d = utc.getUTCDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Whole days from `from` to `to` (yyyy-mm-dd). */
export function calendarDayOffset(from: string, to: string): number {
  return Math.round((calendarDateUtc(to) - calendarDateUtc(from)) / MS_PER_DAY);
}

/**
 * Minutes from midnight of `epochDate` for a stored phase timestamp.
 *
 * Uses the wall-clock date + time digits so an overnight ferment
 * (13:50 → next day 06:00) stays a positive interval instead of wrapping
 * backwards on a single-day clock.
 */
export function absoluteMinutes(
  value?: string | null,
  epochDate?: string
): number | null {
  const clock = clockTimeToMinutes(value ?? undefined);
  if (clock === null) return null;
  const date = extractCalendarDate(value);
  if (!date || !epochDate) return clock;
  return calendarDayOffset(epochDate, date) * MINUTES_PER_DAY + clock;
}

/** Earliest wall-clock day among the drawable phases of these batches. */
export function ganttEpochDate(batches: Partial<TimelineBatch>[]): string | null {
  let min: string | null = null;
  batches.forEach((batch) => {
    batchPhaseRefs(batch).forEach((phase) => {
      if (!isDrawablePhase(phase)) return;
      const date = extractCalendarDate(phase.start) ?? extractCalendarDate(phase.end);
      if (date && (!min || date < min)) min = date;
    });
  });
  return min;
}

/**
 * Keep a phase on the calendar day it starts.
 *
 * An overnight ferment/cool is drawn until midnight of that day, not again
 * on the next morning — that morning is the next phase (pakiranje).
 */
export function clipPhaseToDay(
  phase: GanttPhaseRef,
  day: string
): GanttPhaseRef | null {
  const startDate = extractCalendarDate(phase.start);
  if (!startDate || startDate !== day) return null;
  const endDate = extractCalendarDate(phase.end);
  const end = endDate && endDate > day ? `${day}T24:00:00` : phase.end;
  const clipped = { ...phase, start: phase.start, end };
  return isDrawablePhase(clipped) ? clipped : null;
}

function phaseRefToSegment(phase: GanttPhaseRef) {
  return {
    Code: phase.code,
    Name: phase.name,
    Start: phase.start,
    End: phase.end,
    MachineID: phase.machineId,
    Workers: phase.workers,
    Parallel: phase.parallel,
  };
}

/**
 * Keep only the work that actually runs on `day`.
 *
 * A multi-day batch (panettone mix on Monday, pack on Wednesday) stays in
 * the day's view, but later/earlier phases are dropped so the Gantt axis
 * does not drag the whole product onto every date.
 */
export function clipBatchesToDay(
  batches: Partial<TimelineBatch>[],
  day: string
): Partial<TimelineBatch>[] {
  return batches.flatMap((batch) => {
    const clipped = batchPhaseRefs(batch)
      .map((phase) => clipPhaseToDay(phase, day))
      .filter((phase): phase is GanttPhaseRef => phase != null);
    if (clipped.length === 0) {
      return isSameProductionDay(batch, day) ? [batch] : [];
    }
    return [{ ...batch, Phases: clipped.map(phaseRefToSegment) }];
  });
}

export interface PhaseDayColumn {
  date: string;
  items: { batch: Partial<TimelineBatch>; phase: GanttPhaseRef }[];
}

/** Drawable phases of these batches, grouped by the calendar days they cover. */
export function collectPhaseDays(
  batches: Partial<TimelineBatch>[]
): PhaseDayColumn[] {
  const dates = new Set<string>();
  batches.forEach((batch) => {
    batchPhaseRefs(batch).forEach((phase) => {
      const start = extractCalendarDate(phase.start);
      if (start) dates.add(start);
    });
  });
  return Array.from(dates)
    .sort()
    .map((date) => ({
      date,
      items: batches.flatMap((batch) =>
        batchPhaseRefs(batch)
          .map((phase) => clipPhaseToDay(phase, date))
          .filter((phase): phase is GanttPhaseRef => phase != null)
          .map((phase) => ({ batch, phase }))
      ),
    }));
}

/** Short Croatian day label, e.g. "pon 5. 10." */
export function formatHrDay(date: string): string {
  const utc = new Date(calendarDateUtc(date));
  return utc.toLocaleDateString(dateLocale(), {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    timeZone: "UTC",
  });
}

function machineLaneName(
  machineId: number | null,
  machineNames?: Map<number, string>
): string {
  if (machineId == null) return noMachineLane();
  return machineNames?.get(machineId) || t("Uređaj #{id}", { id: machineId });
}

/**
 * Resource lanes: every phase segment is placed on the lane of the machine
 * (or phase type) that runs it, so one batch appears in several lanes.
 *
 * This is what makes contention visible — two batches fighting over the
 * Chunker, or an oven idle for two hours — which the batch-per-row groupings
 * cannot show, since there each batch draws its whole chain on its mixer lane.
 */
function buildGanttResourceLanes(
  batches: Partial<TimelineBatch>[],
  groupBy: GanttResourceGroupBy,
  machineNames?: Map<number, string>
): GanttLane[] {
  interface LaneBucket {
    laneName: string;
    rank: number;
    firstStart: number;
    rows: Map<number, { row: GanttLaneRow; firstStart: number }>;
  }
  const buckets = new Map<string, LaneBucket>();
  const epoch = ganttEpochDate(batches) ?? "1970-01-01";

  batches.forEach((batch, index) => {
    const batchKey = getBatchKey(batch, index);
    batchPhaseRefs(batch).forEach((phase) => {
      if (!isDrawablePhase(phase)) return;
      const start =
        absoluteMinutes(phase.start, epoch) ??
        (clockTimeToMinutes(phase.start) as number);

      const byMachine = groupBy === "machine";
      const bucketKey = byMachine
        ? `machine:${phase.machineId ?? "none"}`
        : `phase:${phase.code}`;

      let bucket = buckets.get(bucketKey);
      if (!bucket) {
        bucket = {
          laneName: byMachine
            ? machineLaneName(phase.machineId, machineNames)
            : phaseName(phase.code),
          // Machine lanes read as a production flow when ordered by first
          // activity, with the unassigned phases parked at the bottom; phase
          // lanes follow the canonical routing order instead.
          rank: byMachine ? (phase.machineId == null ? 1 : 0) : phaseRank(phase.code),
          firstStart: start,
          rows: new Map(),
        };
        buckets.set(bucketKey, bucket);
      }
      bucket.firstStart = Math.min(bucket.firstStart, start);

      const existing = bucket.rows.get(batchKey);
      if (existing) {
        existing.row.phases.push(phase);
        existing.firstStart = Math.min(existing.firstStart, start);
      } else {
        bucket.rows.set(batchKey, { row: { batch, phases: [phase] }, firstStart: start });
      }
    });
  });

  return Array.from(buckets.values())
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        (groupBy === "machine" ? a.firstStart - b.firstStart : 0) ||
        a.laneName.localeCompare(b.laneName, compareLocale())
    )
    .map((bucket) => ({
      laneName: bucket.laneName,
      rows: Array.from(bucket.rows.values())
        .sort(
          (a, b) =>
            a.firstStart - b.firstStart ||
            (a.row.batch.Sequence ?? 0) - (b.row.batch.Sequence ?? 0)
        )
        .map((entry) => entry.row),
    }));
}

/**
 * Swim-lanes for the planner Gantt in any of the four groupings.
 *
 * `machine`/`phase` scatter each batch's phases across resource lanes;
 * `mixer`/`oven` keep the batch-per-row layout with the full chain in one lane.
 */
export function buildGanttLanes(
  batches: Partial<TimelineBatch>[],
  groupBy: GanttGroupBy,
  machineNames?: Map<number, string>
): GanttLane[] {
  if (isGanttResourceGrouping(groupBy)) {
    return buildGanttResourceLanes(batches, groupBy, machineNames);
  }
  return buildGanttSwimLanes(batches, groupBy).map((lane) => ({
    laneName: lane.deviceName,
    rows: lane.batches.map((batch) => ({ batch, phases: batchPhaseRefs(batch) })),
  }));
}

export function batchesToDailyPlan(batches: Partial<TimelineBatch>[]): DailyPlanEntry[] {
  const byProduct = new Map<number, DailyPlanEntry>();

  batches.forEach((batch) => {
    if (!batch.ProductID || batch.ProductID < 0) return;
    // Polugotovi proizvodi se izvode iz recepture glavnog proizvoda kod
    // generiranja — ne smiju se prikazati kao zasebni redovi dnevnog plana.
    if (batch.is_semi_finished) return;
    const existing = byProduct.get(batch.ProductID);
    if (existing) {
      existing.batch_count += 1;
    } else {
      byProduct.set(batch.ProductID, {
        product_id: batch.ProductID,
        product_name: batch.ProductName || "",
        batch_count: 1,
        mass_kg: batch.FinalMassKg,
        mixer_machine_id: batch.MixerMachineID,
        processing_machine_id: batch.ProcessingMachineID,
        oven_machine_id: batch.OvenMachineID,
        mixing_workers: batch.MixingWorkers,
        processing_workers: batch.ProcessingWorkers,
        baking_workers: batch.BakingWorkers,
      });
    }
  });

  return Array.from(byProduct.values());
}

export function getUniqueMixers(batches: Partial<TimelineBatch>[]): string[] {
  return Array.from(new Set(batches.map((b) => b.MixerName).filter(Boolean))) as string[];
}

export function getUniqueProductNames(batches: Partial<TimelineBatch>[]): string[] {
  return Array.from(new Set(batches.map((b) => b.ProductName).filter(Boolean))) as string[];
}

/** Normalize a timestamp/ISO string to a yyyy-mm-dd value for <input type=date>. */
export function toDateInputValue(value?: string | null): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Real production day (yyyy-mm-dd) of a batch; falls back to the plan day. */
export function batchProductionDay(
  b: Partial<TimelineBatch>,
  fallbackDate: string
): string {
  return toDateInputValue(b.ProductionDate) ?? fallbackDate;
}

/** True when the batch is physically produced on `date` (yyyy-mm-dd). */
export function isSameProductionDay(
  b: Partial<TimelineBatch>,
  date: string
): boolean {
  return batchProductionDay(b, date) === date;
}

/**
 * True when this batch has work that *starts* on `date`.
 *
 * Overnight tails (cooling into the pack morning) stay on the day they began,
 * so the pack day only lists pakiranje.
 */
export function batchActiveOnDay(
  b: Partial<TimelineBatch>,
  date: string
): boolean {
  const dated = batchPhaseRefs(b).filter((phase) => extractCalendarDate(phase.start));
  if (dated.length > 0) {
    return dated.some((phase) => extractCalendarDate(phase.start) === date);
  }
  return isSameProductionDay(b, date);
}

/** A batch represents a semi-finished product (polugotov proizvod). */
export function isSemiFinishedBatch(b: Partial<TimelineBatch>): boolean {
  if (b.IsSemiFinished || b.is_semi_finished) return true;
  return typeof b.ProductID === "number" && b.ProductID < 0;
}

/** Stable grouping key for the semi-finished product behind a batch. */
export function semiFinishedKey(b: Partial<TimelineBatch>): number {
  return (
    b.SemiFinishedID ??
    b.semi_finished_id ??
    (typeof b.ProductID === "number" ? Math.abs(b.ProductID) : 0)
  );
}

export interface SemiFinishedGroup {
  /** Semi-finished product id. */
  key: number;
  name: string;
  /** Planned production day (yyyy-mm-dd), defaulted from the schedule rule. */
  productionDate?: string;
  batchCount: number;
  totalMassKg: number;
}

/**
 * Collapse the semi-finished batches of a generated timeline into one row per
 * semi-finished product, aggregating mass + batch count and surfacing the
 * planned (staggered) production day so it can be reviewed/edited.
 */
export function groupSemiFinishedBatches(
  batches: Partial<TimelineBatch>[],
  fallbackDate?: string
): SemiFinishedGroup[] {
  const map = new Map<number, SemiFinishedGroup>();
  batches.forEach((b) => {
    if (!isSemiFinishedBatch(b)) return;
    const key = semiFinishedKey(b);
    const mass = Number(b.FinalMassKg) || 0;
    const existing = map.get(key);
    if (existing) {
      existing.batchCount += 1;
      existing.totalMassKg += mass;
    } else {
      map.set(key, {
        key,
        name: b.ProductName || t("Polugotov proizvod"),
        productionDate: toDateInputValue(b.ProductionDate) ?? fallbackDate,
        batchCount: 1,
        totalMassKg: mass,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, compareLocale()));
}

/** Apply edited production dates (per semi-finished product) back onto batches. */
export function applySemiFinishedDateEdits(
  batches: Partial<TimelineBatch>[],
  edits: { key: number; productionDate: string }[]
): Partial<TimelineBatch>[] {
  const byKey = new Map(edits.map((e) => [e.key, e.productionDate]));
  return batches.map((b) => {
    if (!isSemiFinishedBatch(b)) return b;
    const date = byKey.get(semiFinishedKey(b));
    if (date === undefined) return b;
    return { ...b, ProductionDate: date };
  });
}

/** Production day chosen for each semi-finished product, keyed by its id. */
export type SemiFinishedDateOverrides = Record<number, string>;

/** Re-apply remembered production days onto a freshly generated batch list. */
export function applySemiFinishedDateOverrides(
  batches: Partial<TimelineBatch>[],
  overrides: SemiFinishedDateOverrides
): Partial<TimelineBatch>[] {
  const edits = Object.entries(overrides).map(([key, productionDate]) => ({
    key: Number(key),
    productionDate,
  }));
  if (edits.length === 0) return batches;
  return applySemiFinishedDateEdits(batches, edits);
}

/**
 * Read the production day already stored on each semi-finished batch.
 *
 * Used to seed the overrides when an existing plan is opened, so that reopening
 * and re-generating a plan keeps the days the planner picked instead of falling
 * back to the schedule rule.
 */
export function collectSemiFinishedDateOverrides(
  batches: Partial<TimelineBatch>[]
): SemiFinishedDateOverrides {
  const overrides: SemiFinishedDateOverrides = {};
  batches.forEach((b) => {
    if (!isSemiFinishedBatch(b)) return;
    const date = toDateInputValue(b.ProductionDate);
    if (date) overrides[semiFinishedKey(b)] = date;
  });
  return overrides;
}

export function groupBatchesByProduct(
  batches: Partial<TimelineBatch>[]
): Record<string, Partial<TimelineBatch>[]> {
  const groups: Record<string, Partial<TimelineBatch>[]> = {};
  batches.forEach((batch) => {
    const product = batch.ProductName || t("Nepoznato");
    if (!groups[product]) groups[product] = [];
    groups[product].push(batch);
  });
  Object.values(groups).forEach((list) =>
    list.sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0))
  );
  return groups;
}

/**
 * Apply order (redoslijed) + mass edits to batches identified by their batch
 * key, regardless of grouping. Re-sorts by the new sequence so downstream
 * recalculation honors the requested order. Used by the per-product phase
 * editor (step 2 of the planning wizard).
 */
export function applyBatchEdits(
  batches: Partial<TimelineBatch>[],
  edits: { batchKey: number; redoslijed: number; mass_kg: number }[]
): Partial<TimelineBatch>[] {
  const byKey = new Map(edits.map((e) => [e.batchKey, e]));
  return batches
    .map((b, i) => {
      const edit = byKey.get(getBatchKey(b, i));
      if (!edit) return b;
      return { ...b, Sequence: edit.redoslijed, FinalMassKg: edit.mass_kg };
    })
    .sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0));
}

export function groupBatchesByMixer(
  batches: Partial<TimelineBatch>[]
): Record<string, Partial<TimelineBatch>[]> {
  const groups: Record<string, Partial<TimelineBatch>[]> = {};
  batches.forEach((batch) => {
    const mixer = batch.MixerName || t("Nepoznato");
    if (!groups[mixer]) groups[mixer] = [];
    groups[mixer].push(batch);
  });
  Object.values(groups).forEach((list) =>
    list.sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0))
  );
  return groups;
}

/** Resolve batch key the same way as GanttPlannerChart / Planning ganttBatches mapping. */
export function getBatchKey(batch: Partial<TimelineBatch>, fallbackIndex?: number): number {
  if (batch.ID) return batch.ID;
  return -(fallbackIndex ?? 0) - 1;
}

/**
 * Ensure every batch carries a stable numeric `ID` key.
 *
 * Engine-generated (unsaved) batches have no DB id, so drag-reorder + the Gantt
 * would otherwise fall back to the array index as a key. That breaks once the
 * Gantt renders a filtered subset (same-day batches only, semi-finished split
 * off to other days): the index in the filtered list no longer matches the
 * index in the full `generatedBatches`, so reorder can't find the dragged/target
 * batch. Assigning a stable negative id by position makes the key independent of
 * any later filtering. Existing ids (saved batches) are kept untouched.
 */
export function assignBatchKeys(
  batches: Partial<TimelineBatch>[]
): Partial<TimelineBatch>[] {
  return batches.map((b, i) => (b.ID != null ? b : { ...b, ID: -(i + 1) }));
}

export function findBatchByKey(
  batches: Partial<TimelineBatch>[],
  key: number
): Partial<TimelineBatch> | undefined {
  return batches.find((b, i) => getBatchKey(b, i) === key);
}

/**
 * Swaps the order (redoslijed) of the dragged and target batches inside the
 * same group (mixer/oven).
 *
 * Ovdje se mijenja samo POREDAK dviju zamjesa u sekvenci miksera; stvarna
 * vremena faza preračunava backend (`reorder` ruta) istim engine-om kao kod
 * generiranja, tako da faze ostanu smisleno posložene bez preklapanja. Ovaj
 * rezultat služi samo kao optimistični prikaz dok backend ne vrati nova vremena.
 */
export function reorderBatchInGroup(
  batches: Partial<TimelineBatch>[],
  draggedKey: number,
  targetKey: number,
  groupBy: GanttBatchGroupBy = "mixer"
): Partial<TimelineBatch>[] {
  const dragged = findBatchByKey(batches, draggedKey);
  const target = findBatchByKey(batches, targetKey);
  if (!dragged || !target || dragged === target) return batches;

  const draggedGroup = getBatchGroupName(dragged, groupBy);
  const targetGroup = getBatchGroupName(target, groupBy);
  if (draggedGroup !== targetGroup) return batches;

  const draggedOrder = dragged.Sequence;
  const targetOrder = target.Sequence;

  return batches
    .map((b, i) => {
      const key = getBatchKey(b, i);
      if (key === draggedKey) return { ...b, Sequence: targetOrder };
      if (key === targetKey) return { ...b, Sequence: draggedOrder };
      return b;
    })
    .sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0));
}

export function reorderBatchInMixer(
  batches: Partial<TimelineBatch>[],
  draggedKey: number,
  targetKey: number
): Partial<TimelineBatch>[] {
  return reorderBatchInGroup(batches, draggedKey, targetKey, "mixer");
}

export function applyMixerEdits(
  batches: Partial<TimelineBatch>[],
  mixer: string,
  edits: { batchKey: number; redoslijed: number; mass_kg: number }[]
): Partial<TimelineBatch>[] {
  const mixerBatches = batches
    .filter((b) => b.MixerName === mixer)
    .map((b) => {
      const key = getBatchKey(b, batches.indexOf(b));
      const edit = edits.find((e) => e.batchKey === key);
      if (!edit) return b;
      return {
        ...b,
        Sequence: edit.redoslijed,
        FinalMassKg: edit.mass_kg,
      };
    })
    .sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0))
    .map((b, i) => ({ ...b, Sequence: i + 1 }));

  const other = batches.filter((b) => b.MixerName !== mixer);
  return [...other, ...mixerBatches].sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0));
}

/**
 * Batches that may still be resequenced during an operational change on a
 * confirmed timeline (planned/ready/on_hold). Finished or running batches are
 * frozen and excluded.
 */
export function filterActiveBatches(
  batches: Partial<TimelineBatch>[]
): Partial<TimelineBatch>[] {
  return batches.filter((b) =>
    OPERATIONAL_ACTIVE_STATUSES.includes((b.BatchStatus ?? "planned") as any)
  );
}

export function toTimelineModificationPayload(
  modifications: TimelineModification[]
): TimelineModification[] {
  return modifications.map((m) => ({
    ...m,
    Broj_zamjesa: m.Broj_zamjesa === "SVE" ? "SVE" : Number(m.Broj_zamjesa),
  }));
}
